import { jsPDF } from "jspdf";
import Konva from "konva";
import { floorplanColors, heatmapColors } from "src/core/enums/floorplan-color-enums";

interface ExportPDFOptions {
  stage: Konva.Stage;
  includeLabels: boolean;
  includeAssets: boolean;
  exportDPI: number;
  fileName: string;
  shapes?: any[]; // Optional shapes data for status-based colors
  type?: "light-status" | "heatmap"; // Type of export
}

function createGradientCircle(
  radius: number,
  colors: [number, string, number, string]
): string {
  const canvas = document.createElement("canvas");
  const size = radius * 2;
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d")!;

  // Keep canvas background transparent and draw a circular background
  ctx.clearRect(0, 0, size, size);
  ctx.imageSmoothingEnabled = true;

  // Create radial gradient for the circle
  const gradient = ctx.createRadialGradient(
    radius,
    radius,
    0, // inner center
    radius,
    radius,
    radius // outer edge
  );

  gradient.addColorStop(colors[0], colors[1]); // "rgba(x)"
  gradient.addColorStop(colors[2], colors[3]); // "rgba(y)"

  // Draw white circular background so gradient blends on white only
  ctx.beginPath();
  ctx.arc(radius, radius, radius, 0, Math.PI * 2);
  ctx.closePath();
  ctx.fillStyle = "#fff";
  ctx.fill();

  // Draw the gradient circle on top of the white background
  ctx.beginPath();
  ctx.arc(radius, radius, radius, 0, Math.PI * 2);
  ctx.closePath();
  ctx.fillStyle = gradient;
  ctx.fill();

  // Outside the circle remains transparent; export PNG keeps transparency
  return canvas.toDataURL("image/png", 1.0);
}

/**
 * Get circle color based on asset status and type
 */
const getAssetCircleColor = (shape: any): string => {
  // const isGateway = shape?.assetType?.toLowerCase() === 'gateway';
  if (shape?.highlightedAsset) {
    return floorplanColors?.selected;
  } else {
    switch (shape?.status) {
      case "On":
        return floorplanColors.lightOn;
      case "Off":
        return floorplanColors.lightOff;
      default:
        return floorplanColors.fault; // Default for other statuses like Initial
    }
  }
};

/**
 * Return the motion gradient color stops array for a shape in heatmap mode
 */
const getMotionCircleColors = (
  shape: any
): [number, string, number, string] => {
  if (!shape)
    return heatmapColors.motion0to25 as [number, string, number, string];

  if (shape?.highlightedAsset)
    return heatmapColors.selected as [number, string, number, string];

  const mc = Number(shape?.motionCount);
  if (Number.isNaN(mc))
    return heatmapColors.motion0to25 as [number, string, number, string];

  if (mc < 26)
    return heatmapColors.motion0to25 as [number, string, number, string];
  if (mc > 25 && mc < 51)
    return heatmapColors.motion26to50 as [number, string, number, string];
  if (mc > 50 && mc < 76)
    return heatmapColors.motion51to75 as [number, string, number, string];
  return heatmapColors.motionMoreThan75 as [number, string, number, string];
};

/**
 * Exports a Konva stage to PDF with hybrid approach: high-res background + vector text
 * @param options - Configuration options for PDF export
 */
export const exportStageToPDF = async (
  options: ExportPDFOptions
): Promise<void> => {
  const {
    stage,
    includeLabels,
    includeAssets,
    exportDPI,
    fileName,
    shapes,
    type,
  } = options;

  if (!stage) {
    throw new Error("Stage is required for PDF export");
  }

  // Get floor image dimensions
  const floorImageNode = stage.findOne(".floor-image");
  const floorImageWidth = floorImageNode?.width() || 889;
  const floorImageHeight = floorImageNode?.height() || 500;

  // Pre-calculate label bounds to determine required canvas size
  const mainLayer: any = stage.findOne("Layer");
  const labelGroups =
    includeLabels && mainLayer ? mainLayer.find(".sensor-label") : [];
  let labelBounds = {
    minX: 0,
    minY: 0,
    maxX: floorImageWidth,
    maxY: floorImageHeight,
  };

  if (includeLabels && labelGroups.length > 0) {
    labelGroups.forEach((g: any) => {
      const x = g.x();
      const y = g.y();
      const rect = g.findOne("Rect");
      const w = rect ? rect.width() : 0;
      const h = rect ? rect.height() : 0;

      labelBounds.minX = Math.min(labelBounds.minX, x);
      labelBounds.minY = Math.min(labelBounds.minY, y);
      labelBounds.maxX = Math.max(labelBounds.maxX, x + w);
      labelBounds.maxY = Math.max(labelBounds.maxY, y + h);
    });
  }

  // Calculate canvas size with padding for labels
  const padding = 20;
  const canvasWidth =
    Math.max(floorImageWidth, labelBounds.maxX - labelBounds.minX) +
    padding * 2;
  const canvasHeight =
    Math.max(floorImageHeight, labelBounds.maxY - labelBounds.minY) +
    padding * 2;

  // Calculate offsets to center content
  const offsetX =
    labelBounds.minX < 0 ? Math.abs(labelBounds.minX) + padding : padding;
  const offsetY =
    labelBounds.minY < 0 ? Math.abs(labelBounds.minY) + padding : padding;

  // Create offscreen container for isolated rendering
  const offscreen = document.createElement("div");
  Object.assign(offscreen.style, {
    position: "fixed",
    left: "-99999px",
    top: "-99999px",
    width: `${canvasWidth}px`,
    height: `${canvasHeight}px`,
  });
  document.body.appendChild(offscreen);

  try {
    // Create offscreen Konva stage
    const exportStage = new Konva.Stage({
      container: offscreen,
      width: canvasWidth,
      height: canvasHeight,
    });

    // Set transparent background to remove borders
    offscreen.style.backgroundColor = "transparent";
    exportStage.container().style.backgroundColor = "transparent";

    // Clone main layer to avoid mutating visible stage
    const stageMaiLayer = stage.findOne("Layer");
    const exportLayer: any = stageMaiLayer?.clone() || new Konva.Layer();
    exportLayer.position({ x: offsetX, y: offsetY });

    // Collect label info for vector text rendering
    const labelGroups = includeLabels ? exportLayer.find(".sensor-label") : [];
    const labels = labelGroups.map((g: any) => {
      const rect: any = g.findOne("Rect");
      const text: any = g.findOne("Text");
      const corner = rect?.cornerRadius?.() ?? 0;
      const cornerToNum = (c: any) =>
        Array.isArray(c) ? c[0] ?? 0 : typeof c === "number" ? c : 0;
      const fontStyle =
        typeof text?.fontStyle === "function"
          ? text.fontStyle()
          : text?.attrs?.fontStyle;
      const align =
        typeof text?.align === "function" ? text.align() : text?.attrs?.align;
      const padding =
        typeof text?.padding === "function"
          ? text.padding()
          : text?.attrs?.padding ?? 6;
      return {
        x: g.x(),
        y: g.y(),
        w: rect ? rect.width() : text?.width() || 0,
        h: rect ? rect.height() : text?.height() || 0,
        rx: cornerToNum(corner),
        ry: cornerToNum(corner),
        stroke: rect?.stroke() || "#bdbdbd",
        strokeWidth: rect?.strokeWidth() || 1,
        fill: rect?.fill() || "#fff",
        text: text?.text() || "",
        fontSize: text?.fontSize() || 12,
        fontWeight: (fontStyle || "").includes("bold") ? "bold" : "normal",
        textFill: text?.fill() || "#333",
        padding,
        align: align || "left",
      };
    });

    // Hide labels for vector rendering later and hide assets since we'll draw circles instead
    exportLayer.find(".sensor-label").forEach((n: any) => n.hide());
    exportLayer.find(".asset-circle").forEach((n: any) => n.hide());

    // Remove shadows and borders for clean output
    exportLayer.find("Rect").forEach((rect: any) => {
      rect.shadowEnabled(false);
      rect.stroke(null);
      rect.strokeWidth(0);
    });

    // Remove any borders from ALL images including floor image
    exportLayer.find("Image").forEach((img: any) => {
      img.stroke(null);
      img.strokeWidth(0);
      img.shadowEnabled(false);
    });

    // Specifically target the floor image to remove borders
    const floorImage = exportLayer.findOne(".floor-image");
    if (floorImage) {
      floorImage.stroke(null);
      floorImage.strokeWidth(0);
      floorImage.shadowEnabled(false);
    }

    exportStage.add(exportLayer);
    exportLayer.draw();

    // Create clean HTML5 canvas for border-free export
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d")!;
    const pixelRatio = Math.min(exportDPI / 96, 20);

    canvas.width = canvasWidth * pixelRatio;
    canvas.height = canvasHeight * pixelRatio;
    canvas.style.width = `${canvasWidth}px`;
    canvas.style.height = `${canvasHeight}px`;

    // Scale context for high DPI
    ctx.scale(pixelRatio, pixelRatio);

    // Fill with white background (no borders)
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, canvasWidth, canvasHeight);

    // Convert Konva stage to image without borders
    const konvaCanvas = exportStage.toCanvas({ pixelRatio: pixelRatio });
    ctx.drawImage(konvaCanvas, 0, 0, canvasWidth, canvasHeight);

    const dataURL = canvas.toDataURL("image/png", 1.0);

    // Initialize PDF
    const orientation = canvasWidth > canvasHeight ? "landscape" : "portrait";
    const pdf = new jsPDF({
      orientation,
      unit: "pt",
      format: [canvasWidth, canvasHeight],
      // compress: true     // compress PDF output for smaller file size
    });

    // Add clean background image (no borders)
    pdf.addImage(dataURL, "PNG", 0, 0, canvasWidth, canvasHeight);

    // Add circular backgrounds for assets to improve clarity
    if (includeAssets && type === "light-status") {
      const assetNodes = exportLayer.find(".asset-circle");
      assetNodes.forEach((node: any, index: number) => {
        // For Konva Circle, x() and y() are already the center coordinates
        const centerX = node.x() + offsetX;
        const centerY = node.y() + offsetY;
        const radius = node.radius();

        // Get corresponding shape data for status-based color
        const shapeData = shapes && shapes[index] ? shapes[index] : null;
        const circleColor = shapeData
          ? getAssetCircleColor(shapeData)
          : floorplanColors.lightOff;

        // Draw status-colored circle with border
        pdf.setFillColor(circleColor);
        pdf.setDrawColor(floorplanColors.border);
        pdf.setLineWidth(0.1);
        pdf.circle(centerX, centerY, radius, "FD"); // 'FD' for fill and draw (border)
      });
    } else if (includeAssets && type === "heatmap") {
      const assetNodes = exportLayer.find(".asset-circle");
      assetNodes.forEach((node: any, index: number) => {
        const centerX = node.x() + offsetX;
        const centerY = node.y() + offsetY;
        const radius = node.radius();

        // Determine which gradient to use
        const gradient = getMotionCircleColors(
          shapes && shapes[index] ? shapes[index] : null
        );

        // Create PNG gradient circle
        const circleImg = createGradientCircle(radius * 30, gradient);

        // Place image centered on circle position
        pdf.addImage(
          circleImg,
          "PNG",
          centerX - radius,
          centerY - radius,
          radius * 2,
          radius * 2
        );

        // Add border on top
        pdf.setDrawColor(heatmapColors.border);
        pdf.setLineWidth(0.05);
        pdf.circle(centerX, centerY, radius, "S"); // stroke only
      });
    }

    // Add vector text labels on top for crisp text
    if (includeLabels && labels.length > 0) {
      labels.forEach((label: any) => {
        // Apply offset to label positions
        const labelX = label.x + offsetX;
        const labelY = label.y + offsetY;

        // Draw rectangle background
        pdf.setDrawColor(label.stroke);
        pdf.setFillColor(label.fill);
        pdf.setLineWidth(label.strokeWidth);
        if (label.rx > 0) {
          // Rounded rectangle (approximation)
          pdf.roundedRect(
            labelX,
            labelY,
            label.w,
            label.h,
            label.rx,
            label.rx,
            "FD"
          );
        } else {
          pdf.rect(labelX, labelY, label.w, label.h, "FD");
        }

        // Draw text
        pdf.setTextColor(label.textFill);
        pdf.setFontSize(label.fontSize);
        pdf.setFont(
          "helvetica",
          label.fontWeight === "bold" ? "bold" : "normal"
        );

        const textX =
          label.align === "center"
            ? labelX + label.w / 2
            : labelX + label.padding;
        const textY = labelY + label.padding + label.fontSize * 0.8; // Adjust baseline
        const textAlign = label.align === "center" ? "center" : "left";

        pdf.text(label.text, textX, textY, { align: textAlign });
      });
    }

    pdf.save(`${fileName}.pdf`);
    exportStage.destroy();
  } finally {
    // Ensure cleanup even if error occurs
    if (offscreen.parentNode) {
      document.body.removeChild(offscreen);
    }
  }
};

/**
 * Generates a formatted filename for PDF export
 * @param blockDetail - Block information
 * @param floor - Floor identifier
 * @returns Formatted filename string
 */
export const generatePDFFileName = (
  blockDetail: any,
  floor?: string
): string => {
  const baseName =
    blockDetail?.blockName && blockDetail?.street && blockDetail?.blockCode
      ? `${blockDetail.blockName}, ${blockDetail.street}, ${blockDetail.blockCode}`
      : "Floor Plan";

  return floor ? `${baseName} - ${floor}` : baseName;
};
